import {useEffect, useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Alert, DialogTitle, Divider, Tab, Tabs, Typography} from "@mui/material";
import {Dialog2} from "../../misc/Dialog2";
import {SmoothActionBar} from "../../misc/SmoothActionBar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {orderByAscending, orderByStrAscending, useAsync2} from "nate-react-api-helpers";
import {api} from "../../api/API";
import {PopperZIndexProvider} from "../../misc/scroller/PopperZIndexProvider";
import {RenderStringCol, StringCol, Table} from "../../misc/scroller/Table";
import {ProductChangeMeta, RecalcItem, RecalcProductMeta} from "../../api/Products";
import {
    useMemoryTableFetcher2
} from "./shopdrawing/freeformchanges/products/add/useMemoryTableFetcher";
import useWindowSize from "react-use/lib/useWindowSize";
import {useBackgroundAction} from "./manage/sov/UseBackgroundTask";
import {useSnackbar} from "../../misc/Snackbar";
import {green, red} from "@mui/material/colors";
import {useAlertProductRecalcState} from "./AlertProductRecalcHeader";
import {CheckboxProvider, CheckboxSelectorCol, useCheckboxSelection} from "./shopdrawing/Checkbox";
import {StatusCol} from "./shopdrawing/openings/Openings";

type RecalcItemExt = RecalcItem & {
    skip?: boolean;
}

export function AlertProductRecalculationRequired2() {
    const {show, hide, projectId, altId, onHide, onDone, loading} = useAlertProductRecalcState();
    if(loading) return null;
    if(hide) return null;
    if(!show) return null;

    return (
        <AlertProductRecalculationRequired
            project={projectId}
            alternative={altId}
            onHide={onHide}
            onDone={onDone}
        />
    )
}

export function AlertProductRecalculationRequired(props: {
    project: number;
    alternative?: number;
    onHide?(): void;
    onDone(success: boolean): void;
}) {
    return (
        <CheckboxProvider>
            <AlertProductRecalculationRequiredInner {...props} />
        </CheckboxProvider>
    )
}

function AlertProductRecalculationRequiredInner(props: {
    project: number;
    alternative?: number;
    onHide?(): void;
    onDone(success: boolean): void;
}) {
    const tableName = "project.recalcDetail"
    const [tab, setTab] = useState("summary");
    const affected = useAsync2((input) => api.products.listForProjectRecalc(input),
        {
            project: props.project,
            alternative: props.alternative
        },
        [props.project, props.alternative]);

    const [affectedList, setAffectedList] = useState<RecalcItemExt[]>([]);

    const serverAffectedList = affected.asList
    useEffect(() => {
        setAffectedList(serverAffectedList)
    }, [serverAffectedList]);

    const ds = useMemoryTableFetcher2(affectedList, setAffectedList);
    const win = useWindowSize();
    const snack = useSnackbar();

    const skipList = useCheckboxSelection();

    const recalc = useBackgroundAction({
        callback: (input: Parameters<typeof api.products.recalcForProject>[0]) => {
            return api.products.recalcForProject(input)
        },
        onError: () => {
            affected.reload();
            setTab("summary");
        },
        onDone: () => {
            snack.success("Recalculation complete!")
            props.onDone(true);
        },
    });

    return (
        <Dialog2 open>
            <DialogTitle>Some Product Details Have Changed</DialogTitle>
            <div>
                <Tabs value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Summary" value="summary" />
                    <Tab label="Affected" value="affected" />
                </Tabs>
                <Divider />

                {affected.LoadingOrErrorElement}

                {tab === "summary" && <div style={{padding: 16}}>
                    <Summary project={props.project} alternative={props.alternative} />
                </div>}
                {tab === "affected" && <div style={{
                    display: "flex",
                    flexDirection: "column",
                    height: win.height * 0.5,
                }}>
                    <PopperZIndexProvider value={1400}>
                        <Alert severity="info">
                            Products will automatically recalculate linked doors/frames.
                        </Alert>
                        {affectedList.length > 0 && <Table<RecalcItemExt>
                            name={tableName}
                            cellCustomize={{
                                backgroundColor: (r) => {
                                    if(!!r.error) return red["50"];
                                    return null;
                                }
                            }}
                            columns={[
                                CheckboxSelectorCol({
                                    header: "Ignore",
                                    width: 80,
                                }),
                                RenderStringCol<RecalcItemExt>("Change", s => s.freeformChange ? "#" + s.freeformChange : ""),
                                StringCol("Opening", "openingName"),
                                StringCol("Product", "categoryName"),
                                StringCol("Code", "productCode"),
                                StringCol("Finish", "finishCode"),
                                StatusCol("Door (active)", "doorActiveStatus"),
                                StatusCol("Door (inactive)", "doorInactiveStatus"),
                                StatusCol("Frame", "frameStatus"),
                                StringCol("Error", "error", 300),
                            ]}
                            fetch={() => ds.fetch()}
                            fetchDeps={[]}
                            onChange={ds.onChange}
                        />}
                    </PopperZIndexProvider>

                </div>}

            </div>
            <SmoothActionBar
                loading={recalc.loading}
                error={recalc.error ? <>
                    You may need to unrelease, or ignore items via the "Affected" tab to resolve this error.
                    <br />
                    <br />
                    {recalc.error}
                </> : undefined}
                actionName="Recalculate"
                cancelName="Hide Temporarily"
                onCancel={props.onHide}
                onSubmit={() => {
                    recalc.callback({
                        project: props.project,
                        alternative: props.alternative,
                        skip: affectedList.filter(v => skipList.includes(v.id)),
                    })
                }}
            />
        </Dialog2>
    )
}

function Summary(props: {
    project: number;
    alternative?: number;
}) {

    const list = useAsync2(input => api.products.listSummaryForProjectRecalc(input),
        {
            project: props.project,
            alternative: props.alternative
        }, [props.project, props.alternative]);

    return (
        <div>
            {list.LoadingOrErrorElement}
            {list.asList.map((itm, index) => {
                const mergedChanges = mergeChanges(itm.changes || []);

                return (
                    <Accordion key={index} disableGutters variant="outlined">
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <div>
                                <Typography variant="body1">
                                    {itm.categoryName} {itm.productCode} {itm.finishCode}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <div>{itm.openingsAffected} {pluralize("opening", itm.openingsAffected)} affected</div>
                                    {!!itm.changesAffected &&
                                        <div>{itm.changesAffected} {pluralize("change", itm.changesAffected)} affected</div>}
                                </Typography>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails>
                            {(itm.changes || [])
                                .filter(change => {
                                    const lines = makeProductChangeLines(change.meta);
                                    return lines.length === 0
                                })
                                .map(change => <div key={change.id}>
                                    {change.description}
                                </div>)}

                            <>
                                <div>
                                    <table style={{width: "100%"}}>
                                        {mergedChanges.length === 0 && <tr>
                                            <td>
                                                Settings were changed and then reset, no effective changes happened.
                                            </td>
                                        </tr>}
                                        {mergedChanges.map((line, j) => {
                                            return (
                                                <tr key={j}>
                                                    <td>{line.name}</td>
                                                    <td style={{
                                                        textDecoration: "line-through",
                                                        color: red["800"]
                                                    }}>{line.old}</td>
                                                    <td style={{
                                                        color: green["900"]
                                                    }}>{line.new}</td>
                                                </tr>
                                            );
                                        })}
                                    </table>
                                </div>
                            </>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </div>
    )
}

type OldNewName = {
    old: string;
    new: string;
    name: string;
}

function mergeChanges(list: RecalcProductMeta[]): OldNewName[] {
    if(!list) return [];
    orderByAscending(list, l => l.id)

    const result: OldNewName[] = [];

    list.forEach(itm => {
        const lines = makeProductChangeLines(itm.meta);

        lines.forEach(line => {
            const existing = result.find(r => r.name === line.name);
            if(existing) {
                existing.new = line.new;
                return;
            }

            result.push(line);
        });
    })

    return result.filter(r => r.new !== r.old);
}

const ignoreList = [
    "Category",
    "CategorySortOrder",
    "FinishName",
    "Series",
]

const hideKeys = [
    "Catalogs",
];

export function makeProductChangeLines(productMeta: ProductChangeMeta | null): OldNewName[] {
    if(!productMeta) return [];

    console.log("meta", productMeta);

    const list = Object.entries(productMeta)
        .filter(([k, v]) => !!v)
        .filter(([k, v]) => !ignoreList.includes(k))
        .filter(([k, v]) => !k.endsWith("CustomTemplateName"))
        .map(([k, v]) => ({
            name: formatName(k),
            old: k.endsWith("CustomTemplate") || hideKeys.includes(k) ? "-" : formatValue(v?.old),
            new: k.endsWith("CustomTemplate") || hideKeys.includes(k) ? "Edited" : formatValue(v?.new),
        }))

    orderByStrAscending(list, l => l.name)

    return list;
}

function formatValue(value: any) {
    if(value === null) return "-"
    if(typeof value === "string") return value;
    if(typeof value === "number") return value.toString();
    if(typeof value === "boolean") return value ? "Yes" : "No";

    return JSON.stringify(value);
}

function formatName(pascalCased: string): string {
    const words = pascalCased.replace(/([A-Z])/g, " $1").split(" ");
    const items = words.join(" ").trim();

    let result = items.charAt(0).toUpperCase() + items.toLowerCase().slice(1);
    result = result.replace("Hw prep", "Preps:")

    return result;
}

export function pluralize(word: string, count: number) {
    if(count === 1) return word;
    return word + "s";
}